/**
 * Suggestion Item Component for v2v2 SWFL Farmers Market Application
 * Individual suggestion row with icon, highlight, and seasonal badge
 */



import { 
  Package, 
  Folder, 
  Store, 
  MapPin, 
  Clock, 
  Leaf,
  Sparkles 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { SearchSuggestion } from '@/types/suggestions';

interface SuggestionItemProps {
  suggestion: SearchSuggestion;
  isSelected: boolean;
  onClick: () => void;
  query: string;
}

const iconMap = {
  product: Package,
  category: Folder,
  vendor: Store,
  market: MapPin,
  recent: Clock,
  seasonal: Leaf,
};

const badgeColors = {
  product: 'bg-sage text-bark dark:bg-forest/40 dark:text-cream',
  category: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  vendor: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  market: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  recent: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300',
  seasonal: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-300',
};

export function SuggestionItem({ 
  suggestion, 
  isSelected, 
  onClick,
  query 
}: SuggestionItemProps) {
  const IconComponent = iconMap[suggestion.type] || Package;
  
  // Highlight matching text
  const renderHighlightedText = () => {
    if (!suggestion.highlightRanges || suggestion.highlightRanges.length === 0) {
      return <span>{suggestion.text}</span>;
    }

    const ranges = suggestion.highlightRanges.sort((a, b) => a[0] - b[0]);
    const parts: JSX.Element[] = [];
    let lastIndex = 0;

    for (const [start, end] of ranges) {
      // Add text before the highlight
      if (start > lastIndex) {
        parts.push(<span key={`text-${lastIndex}`}>{suggestion.text.slice(lastIndex, start)}</span>);
      }
      // Add the highlighted text
      parts.push(
        <span 
          key={`highlight-${start}`} 
          className="font-semibold bg-yellow-200/50 dark:bg-yellow-900/50"
        >
          {suggestion.text.slice(start, end)}
        </span>
      );
      lastIndex = end;
    }

    // Add remaining text
    if (lastIndex < suggestion.text.length) {
      parts.push(<span key={`text-${lastIndex}`}>{suggestion.text.slice(lastIndex)}</span>);
    }

    return parts;
  };

  return (
    <button
      onClick={onClick}
      className={cn(
        'w-full px-4 py-2.5 text-left flex items-center gap-3 transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        isSelected 
          ? 'bg-accent text-accent-foreground' 
          : 'hover:bg-accent/50 hover:text-accent-foreground'
      )}
      aria-selected={isSelected}
      role="option"
    >
      {/* Icon */}
      <div className={cn(
        'flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center',
        badgeColors[suggestion.type]
      )}>
        <IconComponent className="w-4 h-4" />
      </div>

      {/* Text Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium">
            {renderHighlightedText()}
          </span>
          
          {/* Seasonal Badge */}
          {suggestion.seasonalNote && (
            <span className={cn(
              'flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium',
              suggestion.seasonalNote === 'Peak Season!' 
                ? 'bg-green-200 text-green-800 dark:bg-green-800 dark:text-green-200'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200'
            )}>
              {suggestion.seasonalNote === 'Peak Season!' && (
                <Sparkles className="w-3 h-3" />
              )}
              {suggestion.seasonalNote}
            </span>
          )}
        </div>
        
        {/* Category/Location subtitle */}
        {(suggestion.category || suggestion.type === 'market') && (
          <p className="text-xs text-muted-foreground truncate">
            {suggestion.category || suggestion.location}
          </p>
        )}
      </div>
    </button>
  );
}
